import { useCallback, useEffect, useRef, useState } from 'react';
import { DeviceFrame } from '../components/DeviceFrame';
import { MILESTONES } from '../data/journey';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';
import './JourneyStory.css';

const RAIL_WIDTH = 96;
const RAIL_CENTER = RAIL_WIDTH / 2;
const BEND = 18;

/** The line where the route is considered "reached" — just above centre screen. */
const PROGRESS_LINE = 0.55;

/**
 * A gentle S-curve that passes exactly through the centre of every milestone
 * marker, so the drawn route and the markers can never drift apart.
 */
export const buildRoutePath = (anchors, height) => {
  if (!height) return '';
  const points = [0, ...anchors, height];
  let path = `M ${RAIL_CENTER} ${points[0]}`;

  for (let index = 1; index < points.length; index += 1) {
    const from = points[index - 1];
    const to = points[index];
    const span = to - from;
    const bend = index % 2 === 1 ? BEND : -BEND;
    path += ` C ${RAIL_CENTER + bend} ${(from + span * 0.35).toFixed(1)}, ${
      RAIL_CENTER - bend
    } ${(from + span * 0.65).toFixed(1)}, ${RAIL_CENTER} ${to.toFixed(1)}`;
  }

  return path;
};

export const JourneyStory = () => {
  const prefersReducedMotion = usePrefersReducedMotion();

  const sectionRef = useRef(null);
  const listRef = useRef(null);
  const routeRef = useRef(null);
  const itemRefs = useRef([]);
  const markerRefs = useRef([]);

  const [geometry, setGeometry] = useState({ height: 0, anchors: [] });
  const [reached, setReached] = useState(0);
  const [activeIndex, setActiveIndex] = useState(-1);

  const measure = useCallback(() => {
    const list = listRef.current;
    if (!list) return;

    const listTop = list.getBoundingClientRect().top;
    const anchors = markerRefs.current.filter(Boolean).map((marker) => {
      const rect = marker.getBoundingClientRect();
      return rect.top - listTop + rect.height / 2;
    });

    setGeometry((previous) => {
      const height = list.offsetHeight;
      const unchanged =
        previous.height === height &&
        previous.anchors.length === anchors.length &&
        previous.anchors.every((value, index) => Math.abs(value - anchors[index]) < 1);
      return unchanged ? previous : { height, anchors };
    });
  }, []);

  useEffect(() => {
    measure();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }

    const observer = new ResizeObserver(measure);
    if (listRef.current) observer.observe(listRef.current);
    return () => observer.disconnect();
  }, [measure]);

  // Scroll-linked route progress. Written straight to the DOM so scrolling never
  // re-renders React; only the discrete milestone states are state.
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return undefined;

    const setProgress = (value) => {
      section.style.setProperty('--tl-progress', String(value));
      if (routeRef.current) {
        routeRef.current.style.strokeDashoffset = String(1 - value);
      }
    };

    if (prefersReducedMotion) {
      setProgress(1);
      setReached(MILESTONES.length);
      setActiveIndex(-1);
      return undefined;
    }

    let frame = null;

    const update = () => {
      frame = null;
      const list = listRef.current;
      if (!list) return;

      const rect = list.getBoundingClientRect();
      const line = window.innerHeight * PROGRESS_LINE;
      const progress = Math.min(1, Math.max(0, (line - rect.top) / (rect.height || 1)));
      setProgress(progress);

      let reachedCount = 0;
      let nearestIndex = -1;
      let nearestDistance = Infinity;
      const viewportMiddle = window.innerHeight / 2;
      const bandTop = window.innerHeight * 0.225;
      const bandBottom = window.innerHeight * 0.775;

      markerRefs.current.forEach((marker, index) => {
        if (!marker) return;
        const markerRect = marker.getBoundingClientRect();
        if (markerRect.top + markerRect.height / 2 <= line) reachedCount += 1;

        const item = itemRefs.current[index];
        if (!item) return;
        const itemRect = item.getBoundingClientRect();
        if (itemRect.bottom < bandTop || itemRect.top > bandBottom) return;

        const distance = Math.abs(itemRect.top + itemRect.height / 2 - viewportMiddle);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      setReached((previous) => (previous === reachedCount ? previous : reachedCount));
      setActiveIndex((previous) => (previous === nearestIndex ? previous : nearestIndex));
    };

    const onScroll = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [prefersReducedMotion, geometry.height]);

  const routePath = buildRoutePath(geometry.anchors, geometry.height);

  return (
    <section
      className="tl-journey"
      id="journey"
      ref={sectionRef}
      aria-labelledby="tl-journey-heading"
    >
      <div className="tl-shell">
        <h2 className="tl-visually-hidden" id="tl-journey-heading">
          Together from start to finish
        </h2>

        <div className="tl-journey__body">
          {geometry.height > 0 && (
            <div className="tl-journey__rail" aria-hidden="true">
              <svg
                className="tl-journey__route-svg"
                width={RAIL_WIDTH}
                height={geometry.height}
                viewBox={`0 0 ${RAIL_WIDTH} ${geometry.height}`}
                fill="none"
              >
                <defs>
                  <linearGradient id="tl-route-gradient" x1="0" y1="0" x2="0" y2="1">
                    {/* Stop colours come from CSS so they can use the brand tokens. */}
                    <stop className="tl-journey__stop-start" offset="0%" />
                    <stop className="tl-journey__stop-mid" offset="45%" />
                    <stop className="tl-journey__stop-end" offset="100%" />
                  </linearGradient>
                </defs>
                <path className="tl-journey__route-track" d={routePath} />
                <path
                  className="tl-journey__route"
                  ref={routeRef}
                  d={routePath}
                  pathLength="1"
                  stroke="url(#tl-route-gradient)"
                />
              </svg>
            </div>
          )}

          <ol className="tl-journey__list" ref={listRef}>
            {MILESTONES.map((milestone, index) => {
              const isReached = index < reached;
              const isActive = index === activeIndex;

              return (
                <li
                  key={milestone.id}
                  id={milestone.id}
                  className={`tl-journey__item${isActive ? ' is-active' : ''}${
                    isReached ? ' is-reached' : ''
                  }`}
                  ref={(node) => {
                    itemRefs.current[index] = node;
                  }}
                  aria-current={isActive ? 'step' : undefined}
                >
                  <div className="tl-journey__story tl-reveal">
                    <p className="tl-eyebrow">{milestone.eyebrow}</p>
                    <h3 className="tl-journey__heading">{milestone.heading}</h3>
                    <p className="tl-lede">{milestone.body}</p>
                    <figure className="tl-journey__figure">
                      <img
                        className="tl-photo"
                        src={milestone.photo.src}
                        alt={milestone.photo.alt}
                        width={milestone.photo.width}
                        height={milestone.photo.height}
                        style={{ objectPosition: milestone.photo.objectPosition }}
                        loading="lazy"
                        decoding="async"
                      />
                    </figure>
                  </div>

                  <div
                    className="tl-journey__marker"
                    aria-hidden="true"
                    ref={(node) => {
                      markerRefs.current[index] = node;
                    }}
                  >
                    <span className="tl-journey__marker-dot">{index + 1}</span>
                  </div>

                  <div className="tl-journey__product tl-reveal">
                    <DeviceFrame
                      src={milestone.product.src}
                      video={milestone.product.video}
                      alt={milestone.product.alt}
                      width={milestone.product.width}
                      height={milestone.product.height}
                    />
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
};
